import { useState } from 'react';

import { DATE_FORMAT_LONG, dateFormat, paymentAmountFormat } from '@willow/shared-web';

import { WillowModal } from '../../modal/Modal';
import { PaymentType, PaymentTypeLabels } from '../helpers';
import {
  getPaymentConfirmCopy,
  getPaymentConfirmStatus,
  getPaymentConfirmTitle,
  PAYMENT_CONFIRM_CANCEL_LABELS,
  PAYMENT_CONFIRM_SUBMIT_LABELS,
} from './paymentConfirmHelpers';

import './PaymentConfirmModal.scss';

interface Props {
  showModal: boolean;
  onClose: () => void;
  paymentAmount: number;
  paymentType: PaymentType;
  bankName: string;
  autopayEnabled: boolean;
  amountDue: number;
  onConfirm: () => Promise<void>;
}

export const PaymentConfirmModal = ({
  showModal,
  onClose,
  paymentAmount,
  paymentType,
  bankName,
  autopayEnabled,
  amountDue,
  onConfirm,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { status, overpaymentAmount } = getPaymentConfirmStatus({
    paymentType,
    paymentAmount,
    amountDue,
    autopayEnabled,
  });

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WillowModal showModal={showModal} onClose={onClose}>
      <div className="payment-confirm-modal">
        <h2>{getPaymentConfirmTitle(status)}</h2>

        <div className="payment-confirm-modal__copy">
          {getPaymentConfirmCopy({ status, overpaymentAmount, amountDue, autopayEnabled })}
        </div>

        <ul className="payment-confirm-modal__details">
          <li>
            <div className="payment-confirm-modal__details__title">Payment Type</div>
            <div className="payment-confirm-modal__details__desc">{PaymentTypeLabels[paymentType]}</div>
          </li>
          <li>
            <div className="payment-confirm-modal__details__title">Amount</div>
            <div className="payment-confirm-modal__details__desc">{paymentAmountFormat(paymentAmount)}</div>
          </li>
          <li>
            <div className="payment-confirm-modal__details__title">From account</div>
            <div className="payment-confirm-modal__details__desc">{bankName}</div>
          </li>
          <li>
            <div className="payment-confirm-modal__details__title">Processing date</div>
            <div className="payment-confirm-modal__details__desc">{dateFormat(new Date(), DATE_FORMAT_LONG)}</div>
          </li>
        </ul>

        <div className="payment-confirm-modal__buttons">
          <button type="button" onClick={onClose} className="payment-confirm-modal__cancel-btn">
            {PAYMENT_CONFIRM_CANCEL_LABELS[status]}
          </button>

          <button
            disabled={isSubmitting}
            onClick={handleConfirm}
            className="payment-confirm-modal__submit-btn"
            data-ghost="portal-loan--modal-submit-payment-button"
          >
            {isSubmitting ? 'Processing...' : PAYMENT_CONFIRM_SUBMIT_LABELS[status]}
          </button>
        </div>
      </div>
    </WillowModal>
  );
};
